import type { ImportBatch, ImportBatchView, ImportMappingsView } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileSpreadsheet, Upload } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { callAction } from "../api";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, EmptyState, ErrorState, Input, LoadingState } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { fileToBase64, shortDate } from "../theme";

export function statusTone(s: ImportBatch["status"]): "neutral" | "green" | "amber" | "red" | "blue" {
  return s === "imported" ? "green" : s === "validated" ? "blue" : s === "failed" ? "red" : s === "mapped" ? "amber" : "neutral";
}

export function ImportScreen() {
  const t = useT();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const batches = useQuery({ queryKey: ["imports"], queryFn: async () => (await callAction<"agency.import.list", ImportBatch[]>("agency.import.list", { limit: 50 })).result });
  const mappings = useQuery({ queryKey: ["import-mappings"], queryFn: async () => (await callAction<"agency.import.mappings", ImportMappingsView>("agency.import.mappings", {})).result, staleTime: 60_000 });

  const [sourceSystem, setSourceSystem] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [mappingId, setMappingId] = useState<string | undefined>(undefined);

  const upload = useMutation({
    mutationFn: async (v: { sourceSystem: string; filename: string; base64: string; mappingId?: string }) => callAction<"agency.import.upload", ImportBatchView>("agency.import.upload", v),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["imports"] });
      navigate(`/import/${r.result.batch.id}`);
    },
    onError: (e) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" }),
  });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast({ title: t("common.error"), body: "10 MB", tone: "error" });
    const base64 = await fileToBase64(file);
    upload.mutate({ sourceSystem: sourceSystem.trim() || file.name.replace(/\.[^.]+$/, ""), filename: file.name, base64, mappingId });
  };

  const useSample = async (sampleId: string) => {
    const s = mappings.data?.samples.find((x) => x.id === sampleId);
    if (!s) return;
    const res = await fetch(s.url);
    if (!res.ok) return toast({ title: t("common.error"), body: `${s.url} (${res.status})`, tone: "error" });
    const text = await res.text();
    const base64 = btoa(unescape(encodeURIComponent(text)));
    upload.mutate({ sourceSystem: s.sourceSystem, filename: s.url.split("/").pop() ?? "sample.csv", base64, mappingId: s.mappingId });
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold">{t("import.title")}</h1>
        <p className="text-ink/60">{t("import.subtitle")}</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader title={t("import.new")} />
          <form className="px-5 pb-5 space-y-3" onSubmit={submit}>
            <Input label={t("import.sourceSystem")} value={sourceSystem} onChange={(e) => setSourceSystem(e.target.value)} placeholder="agency-matrix, carrier portal, spreadsheet…" />
            <p className="text-xs text-ink/55 -mt-1">{t("import.sourceSystem.help")}</p>
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-ink/80">{t("import.file")}</span>
              <input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] ?? null)} className="block w-full text-sm file:mr-3 file:min-h-[40px] file:rounded-xl file:border-0 file:bg-ink file:px-3 file:text-white file:font-semibold" />
            </label>
            {mappings.data && mappings.data.mappings.length ? (
              <label className="block">
                <span className="mb-1 block text-sm font-semibold text-ink/80">{t("import.map.saved")}</span>
                <select className="w-full min-h-tap rounded-xl border border-ink/15 bg-white px-3 text-base" value={mappingId ?? ""} onChange={(e) => setMappingId(e.target.value || undefined)}>
                  <option value="">{t("import.map.useSuggested")}</option>
                  {mappings.data.mappings.map((m) => <option key={m.id} value={m.id}>{m.name}{m.builtIn ? ` (${t("import.mapping.builtin")})` : ""}</option>)}
                </select>
              </label>
            ) : null}
            <Button type="submit" disabled={!file} loading={upload.isPending}><Upload className="h-4 w-4" />{upload.isPending ? t("import.uploading") : t("import.upload")}</Button>
          </form>
        </Card>

        <Card>
          <CardHeader title={t("import.samples")} subtitle={t("import.samples.sub")} />
          <ul className="px-5 pb-5 space-y-3">
            {mappings.isLoading ? <li className="text-sm text-ink/55">{t("common.loading")}</li> : null}
            {mappings.data?.samples.map((s) => (
              <li key={s.id} className="rounded-2xl border border-ink/10 p-3">
                <p className="font-semibold inline-flex items-center gap-2"><FileSpreadsheet className="h-4 w-4 text-ink/50" />{s.name}</p>
                <p className="text-sm text-ink/70">{s.description}</p>
                <p className="text-xs text-ink/50 mt-1">{s.note}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => useSample(s.id)} loading={upload.isPending}>{t("import.useSample")}</Button>
                  <a className="text-xs text-primary-dark hover:underline" href={s.url} target="_blank" rel="noopener">CSV ↗</a>
                </div>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <Card>
        <CardHeader title={t("import.history")} />
        {batches.isLoading ? <LoadingState /> : null}
        {batches.isError ? <div className="px-5 pb-5"><ErrorState error={batches.error} onRetry={() => batches.refetch()} /></div> : null}
        {batches.data && batches.data.length === 0 ? <div className="px-5 pb-5"><EmptyState title={t("import.none")} /></div> : null}
        {batches.data && batches.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-ink/55">
                <tr>
                  <th className="px-4 py-2">{t("common.file")}</th>
                  <th className="px-4 py-2 hidden sm:table-cell">{t("import.sourceSystem")}</th>
                  <th className="px-4 py-2">{t("common.status")}</th>
                  <th className="px-4 py-2 hidden md:table-cell">{t("import.rows")}</th>
                  <th className="px-4 py-2 hidden md:table-cell">{t("import.counts.newCustomers")} / {t("import.counts.newPolicies")}</th>
                  <th className="px-4 py-2 hidden sm:table-cell">{t("common.due")}</th>
                </tr>
              </thead>
              <tbody>
                {batches.data.map((b) => (
                  <tr key={b.id} className="border-t border-ink/5 hover:bg-primary/5">
                    <td className="px-4 py-2"><Link to={`/import/${b.id}`} className="font-semibold hover:underline block min-h-[36px] leading-[36px] truncate max-w-[260px]">{b.filename}</Link><span className="text-xs text-ink/50">{t(`import.origin.${b.origin}`)}</span></td>
                    <td className="px-4 py-2 hidden sm:table-cell font-mono text-xs">{b.sourceSystem}</td>
                    <td className="px-4 py-2"><Badge tone={statusTone(b.status)}>{t(`import.status.${b.status}`)}</Badge></td>
                    <td className="px-4 py-2 hidden md:table-cell">{b.rowCount}</td>
                    <td className="px-4 py-2 hidden md:table-cell">{b.result ? `${b.result.created.customers} / ${b.result.created.policies}` : b.validation ? `${b.validation.creates.customers} / ${b.validation.creates.policies} (dry run)` : "—"}</td>
                    <td className="px-4 py-2 hidden sm:table-cell">{shortDate(b.createdAt, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
